'use client';
import styles from './ProductOptionsRadio.module.scss';
import classNames from 'classnames';
import { Entry } from 'contentful';
import { Document } from '@contentful/rich-text-types';

import { removeCentsFromAmount } from '@/utils/currency';
import { ShopifyVariant, ShopifyProduct } from '@/lib/shopify';
import { TypeProduct, TypeProductFields } from '@/core/generated-types';
import { RichText } from '../RichText';
import { RadioButton } from '../RadioButton';
import { formatMoney } from '@/utils/helpers';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import useAuth from '@/context/AuthContext';

interface ProductOptionRadioInterface {
  products: Entry<TypeProductFields>[];
  productSelected: Entry<TypeProductFields>;
  shopifyProducts: ShopifyProduct[];
  onChange: (
    product: TypeProduct,
    shopifyProduct: ShopifyProduct,
    variant: ShopifyVariant,
  ) => void;
}

function ProductOptionRadio({
  products,
  productSelected,
  shopifyProducts,
  onChange,
}: ProductOptionRadioInterface) {
  const { user } = useAuth();
  const router = useRouter();
  const { locale } = useRouter();

  useEffect(() => {
    const slug = router.query?.slug ?? '';
    if (
      !user?.attributes.loveveryId &&
      (slug.includes('part-1') ||
        slug.includes('part-2') ||
        slug.includes('part-3'))
    ) {
      window.localStorage.setItem('rssReturnTo', `/products/${slug}`);
    }
  }, [router, user]);

  return (
    <div className="mb-4" test-id="product-options">
      {products &&
        products.map((product, index) => {
          const id = product.sys.id;
          const shopifyProduct = shopifyProducts[index];
          const variant = shopifyProduct.variants[0];

          // The first variant is always used because the others
          // do not fit into the radio options design proposed
          const formattedPrice = variant.formattedPrice;

          const { title, optionDetails, strikethroughPrice } = product.fields;
          const checked = productSelected?.sys.id === id;

          return (
            <div
              key={id}
              className={classNames(
                'border rounded-[0.3125rem] pl-3 pr-5 mb-4 last:mb-0',
                checked
                  ? 'border-blue-deep bg-indigo-pastel outline outline-blue-deep'
                  : 'border-gray-500',
              )}
            >
              <RadioButton
                id={id}
                Content={() => (
                  <div className="flex gap-1">
                    <div className="flex-auto">
                      <div className="text-[1.25rem] pr-8">{title}</div>
                      {optionDetails && (
                        <div className="font-normal mt-4 -ml-9">
                          <RichText
                            content={optionDetails as Document}
                            addParagraphMargins={false}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end flex-none">
                      <div className="text-2xl">
                        {formattedPrice &&
                          removeCentsFromAmount(formattedPrice)}
                      </div>
                      {strikethroughPrice && (
                        <div className="text-lg line-through font-normal text-[#6E757C]">
                          {formatMoney(strikethroughPrice, locale, true)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                radioAlignment="top"
                className={`py-6 ${styles.optionRadio}`}
                checked={checked}
                onChange={() => {
                  onChange(product, shopifyProduct, variant);
                }}
                boldLabel
              />
            </div>
          );
        })}
    </div>
  );
}

export default ProductOptionRadio;
